/**
 * Announcements Management Page
 *
 * Full CRUD, real-time search suggest, category/priority/target filters,
 * interactive status switch, and modal forms.
 * Table with no-line rule: uses tonal surfaces to separate rows.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Loader2, Trash2, Edit2, Eye, Megaphone, AlertCircle, X } from 'lucide-react';
import { Button } from '@lms/ui';
import {
  useAnnouncements,
  useDeleteAnnouncement,
  useToggleAnnouncement,
} from '../hooks/useAnnouncements';
import { AnnouncementFormModal } from '../components/AnnouncementFormModal';
import { AnnouncementDetailModal } from '../components/AnnouncementDetailModal';
import { Pagination } from '../components/Pagination';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type {
  Announcement,
  AnnouncementCategory,
  AnnouncementPriority,
  AnnouncementTarget,
} from '@lms/shared/types';

export function Announcements() {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [isSuggestOpen, setIsSuggestOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [viewingAnnouncement, setViewingAnnouncement] = useState<Announcement | null>(null);

  const limit = 10;
  const { language, t } = useLanguage();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside suggestion
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setIsSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, isLoading } = useAnnouncements({
    page,
    limit,
    search: debouncedSearch || undefined,
    category: (selectedCategory as AnnouncementCategory) || undefined,
    priority: (selectedPriority as AnnouncementPriority) || undefined,
    targetAudience: (selectedTarget as AnnouncementTarget) || undefined,
  });

  const { data: suggestionsData } = useAnnouncements({
    search: searchQuery.trim() || undefined,
    limit: 6,
  });
  const suggestedItems = suggestionsData?.items || [];

  const { mutate: deleteAnnouncement } = useDeleteAnnouncement();
  const { mutate: toggleActive } = useToggleAnnouncement();
  const announcements = data?.items || [];

  const formatDate = (date: Date | string | undefined | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPriorityBadge = (priority: AnnouncementPriority) => {
    switch (priority) {
      case 'URGENT':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-error-container text-on-error-container uppercase tracking-wider">
            <AlertCircle size={11} />
            {t(copy.announcements.prioUrgent)}
          </span>
        );
      case 'HIGH':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-warning-container text-on-warning-container uppercase tracking-wider">
            {t(copy.announcements.prioHigh)}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-surface-container-high text-on-surface-variant uppercase tracking-wider">
            {t(copy.announcements.prioNormal)}
          </span>
        );
    }
  };

  const getCategoryLabel = (category: AnnouncementCategory) => {
    switch (category) {
      case 'ACADEMIC':
        return t(copy.announcements.catAcademic);
      case 'IMPORTANT':
        return t(copy.announcements.catImportant);
      case 'MAINTENANCE':
        return t(copy.announcements.catMaintenance);
      case 'EVENT':
        return t(copy.announcements.catEvent);
      default:
        return t(copy.announcements.catGeneral);
    }
  };

  const getTargetLabel = (target: AnnouncementTarget) => {
    switch (target) {
      case 'PARTICIPANT_ONLY':
        return t(copy.announcements.targetParticipant);
      case 'TEACHER_ONLY':
        return t(copy.announcements.targetTeacher);
      case 'ADMIN_ONLY':
        return t(copy.announcements.targetAdmin);
      default:
        return t(copy.announcements.targetAll);
    }
  };

  const headers = [
    copy.announcements.tableHeaderTitle,
    copy.announcements.tableHeaderCategory,
    copy.announcements.tableHeaderPriority,
    copy.announcements.tableHeaderTarget,
    copy.announcements.tableHeaderStatus,
    copy.announcements.tableHeaderDate,
    { id: '', ar: '' },
  ];

  return (
    <div className="space-y-12 animate-fadeIn">
      {/* Editorial Header */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-10 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <h1 className="text-4xl font-headline font-extrabold text-on-surface tracking-tight leading-none m-0">
            <Bilingual {...copy.announcements.listTitle} />
          </h1>
          <p className="text-lg font-body text-on-surface-variant max-w-2xl">
            <Bilingual {...copy.announcements.listSubtitle} />
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingAnnouncement(null);
            setIsFormModalOpen(true);
          }}
          className="bg-primary text-on-primary h-12 px-8 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-[0.15em] no-underline shadow-level1 hover:bg-primary-container active:scale-95 transition-all"
        >
          <Plus size={18} />
          <Bilingual {...copy.announcements.createBtn} />
        </Button>
      </section>

      {/* Control Surface with Search & Filter */}
      <section className="flex flex-wrap items-center justify-between gap-6">
        {/* Search Suggest Input */}
        <div className="relative w-80 sm:w-96 group" ref={searchContainerRef}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 size-4 group-focus-within:text-primary transition-all" />
            <input
              aria-label="Search announcements"
              className="w-full bg-surface-container-low border-none rounded-2xl pl-11 pr-10 py-3 text-sm font-body focus:ring-2 focus:ring-primary/20 transition-all outline-none shadow-sm placeholder:text-on-surface-variant/40"
              placeholder={t(copy.announcements.searchPlaceholder)}
              type="text"
              value={searchQuery}
              onFocus={() => {
                if (searchQuery.trim().length > 0) setIsSuggestOpen(true);
              }}
              onChange={e => {
                setSearchQuery(e.target.value);
                setIsSuggestOpen(e.target.value.trim().length > 0);
              }}
              onKeyDown={e => {
                if (e.key === 'Escape') setIsSuggestOpen(false);
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setIsSuggestOpen(false);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-on-surface-variant hover:text-error transition-colors rounded-full"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Autocomplete Suggestion Dropdown */}
          {isSuggestOpen && searchQuery.trim().length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-surface-container-lowest rounded-2xl p-2 shadow-level4 border border-on-surface/5 max-h-80 overflow-y-auto animate-fadeIn">
              <div className="px-3 py-1.5 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant/60 flex items-center gap-1.5 border-b border-on-surface/5 mb-1">
                <Megaphone size={12} className="text-primary" />
                <Bilingual {...copy.announcements.listTitle} />
              </div>
              {suggestedItems.length > 0 ? (
                suggestedItems.map(item => (
                  <div
                    key={item.id}
                    onClick={() => {
                      setSearchQuery(item.title);
                      setIsSuggestOpen(false);
                    }}
                    className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-surface-container-high text-on-surface transition-colors cursor-pointer group/item"
                  >
                    <div className="flex flex-col min-w-0 pr-3">
                      <span className="truncate font-body font-bold text-sm group-hover/item:text-primary transition-colors">
                        {item.title}
                      </span>
                      <span className="text-[11px] font-body text-on-surface-variant line-clamp-1 opacity-70 mt-0.5">
                        {item.content}
                      </span>
                    </div>
                    {getPriorityBadge(item.priority)}
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-xs text-on-surface-variant font-body">
                  <Bilingual {...copy.programs.noSuggestions} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-label font-bold text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
          >
            <option value="">{t(copy.announcements.filterAllCategories)}</option>
            <option value="GENERAL">{t(copy.announcements.catGeneral)}</option>
            <option value="ACADEMIC">{t(copy.announcements.catAcademic)}</option>
            <option value="IMPORTANT">{t(copy.announcements.catImportant)}</option>
            <option value="MAINTENANCE">{t(copy.announcements.catMaintenance)}</option>
            <option value="EVENT">{t(copy.announcements.catEvent)}</option>
          </select>

          <select
            value={selectedPriority}
            onChange={e => setSelectedPriority(e.target.value)}
            className="bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-label font-bold text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
          >
            <option value="">{t(copy.announcements.filterAllPriorities)}</option>
            <option value="NORMAL">{t(copy.announcements.prioNormal)}</option>
            <option value="HIGH">{t(copy.announcements.prioHigh)}</option>
            <option value="URGENT">{t(copy.announcements.prioUrgent)}</option>
          </select>

          <select
            value={selectedTarget}
            onChange={e => setSelectedTarget(e.target.value)}
            className="bg-surface-container-low border-none rounded-xl px-4 py-2.5 text-xs font-label font-bold text-on-surface focus:ring-2 focus:ring-primary/20 outline-none"
          >
            <option value="">{t(copy.announcements.filterAllAudiences)}</option>
            <option value="ALL">{t(copy.announcements.targetAll)}</option>
            <option value="PARTICIPANT_ONLY">{t(copy.announcements.targetParticipant)}</option>
            <option value="TEACHER_ONLY">{t(copy.announcements.targetTeacher)}</option>
            <option value="ADMIN_ONLY">{t(copy.announcements.targetAdmin)}</option>
          </select>
        </div>
      </section>

      {/* Operational Matrix */}
      <div className="bg-surface-container-lowest rounded-2xl overflow-hidden shadow-level1 border border-on-surface/5">
        {/* Table Header */}
        <div className="hidden md:grid grid-cols-[2.5fr_1fr_1fr_1.2fr_1fr_1fr_100px] gap-6 px-8 py-5 bg-surface-container-low/50 border-b border-on-surface/5">
          {headers.map((h, i) => (
            <div
              key={i}
              className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest"
            >
              <Bilingual {...h} />
            </div>
          ))}
        </div>

        {/* Table Rows */}
        <div className="divide-y divide-on-surface/5">
          {isLoading && announcements.length === 0 ? (
            <div className="p-16 flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm font-body text-on-surface-variant">
                <Bilingual {...copy.common.loading} />
              </p>
            </div>
          ) : announcements.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <p className="text-sm font-body text-on-surface-variant">
                <Bilingual {...copy.announcements.noAnnouncements} />
              </p>
            </div>
          ) : (
            announcements.map(announcement => (
              <div
                key={announcement.id}
                className="flex flex-col md:grid md:grid-cols-[2.5fr_1fr_1fr_1.2fr_1fr_1fr_100px] gap-4 md:gap-6 p-6 md:px-8 md:py-5 hover:bg-surface-container-low transition-colors duration-150 items-start md:items-center group"
              >
                {/* Title & Preview */}
                <div className="w-full flex justify-between items-start md:block">
                  <div>
                    <div
                      onClick={() => setViewingAnnouncement(announcement)}
                      className="text-sm font-body font-bold text-on-surface group-hover:text-primary transition-colors cursor-pointer pr-4 md:pr-0"
                    >
                      {announcement.title}
                    </div>
                    <p className="text-[11px] font-body text-on-surface-variant line-clamp-1 mt-0.5 opacity-60">
                      {announcement.content}
                    </p>
                  </div>
                </div>

                {/* Category */}
                <div className="flex items-center justify-between w-full md:w-auto">
                  <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    <Bilingual {...copy.announcements.tableHeaderCategory} />
                  </span>
                  <span className="text-xs font-body font-medium text-on-surface bg-surface-container-high px-2.5 py-1 rounded-lg">
                    {getCategoryLabel(announcement.category)}
                  </span>
                </div>

                {/* Priority */}
                <div className="flex items-center justify-between w-full md:w-auto">
                  <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    <Bilingual {...copy.announcements.tableHeaderPriority} />
                  </span>
                  {getPriorityBadge(announcement.priority)}
                </div>

                {/* Target Audience */}
                <div className="flex items-center justify-between w-full md:w-auto">
                  <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    <Bilingual {...copy.announcements.tableHeaderTarget} />
                  </span>
                  <div className="text-xs font-body text-on-surface-variant">
                    {getTargetLabel(announcement.targetAudience)}
                    {announcement.program && (
                      <span className="block text-[10px] font-mono text-primary font-bold">
                        [{announcement.program.code}]
                      </span>
                    )}
                  </div>
                </div>

                {/* Status Switch */}
                <div className="flex items-center justify-between w-full md:w-auto">
                  <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    <Bilingual {...copy.announcements.tableHeaderStatus} />
                  </span>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={announcement.isActive}
                      onChange={() => toggleActive(announcement.id)}
                    />
                    <div className="w-9 h-5 bg-surface-container-high peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary relative" />
                  </label>
                </div>

                {/* Date */}
                <div className="flex items-center justify-between w-full md:w-auto">
                  <span className="md:hidden text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest">
                    <Bilingual {...copy.announcements.tableHeaderDate} />
                  </span>
                  <div className="text-xs font-body text-on-surface-variant">
                    {formatDate(announcement.createdAt)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-1.5 w-full md:w-auto pt-2 md:pt-0 border-t md:border-t-0 border-on-surface/5">
                  <button
                    onClick={() => setViewingAnnouncement(announcement)}
                    className="p-1.5 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-colors"
                    title={t(copy.announcements.readMore)}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setEditingAnnouncement(announcement);
                      setIsFormModalOpen(true);
                    }}
                    className="p-1.5 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-colors"
                    title={t(copy.announcements.editBtn)}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t(copy.announcements.deleteConfirm))) {
                        deleteAnnouncement(announcement.id);
                      }
                    }}
                    className="p-1.5 text-error hover:bg-error/10 rounded-lg transition-colors"
                    title={t(copy.common.delete)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {data?.meta && (
          <Pagination
            currentPage={data.meta.page}
            totalPages={data.meta.totalPages}
            onPageChange={setPage}
          />
        )}
      </div>

      {/* Form Modal */}
      <AnnouncementFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingAnnouncement(null);
        }}
        announcementToEdit={editingAnnouncement}
      />

      {/* Detail Modal */}
      <AnnouncementDetailModal
        announcement={viewingAnnouncement}
        isOpen={!!viewingAnnouncement}
        onClose={() => setViewingAnnouncement(null)}
      />
    </div>
  );
}
